import React, { useState, useEffect } from "react";
import { Card, Table, Pagination, Spinner, Alert, OverlayTrigger, Tooltip } from "react-bootstrap"; // Added OverlayTrigger and Tooltip
import { FaHistory } from "react-icons/fa";
import { runData } from "../../functions/api/processor";
import { useReport } from "../../context/ReportModal";
import moment from "moment"; // For date formatting

const LoginHistoryCard = () => {
  const [history, setHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setReport } = useReport();

  const fetchHistory = async (page) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await runData({page: page}, `/api/profile/history/`, "GET");
      if (response.status === 200 && response.data) {
        setHistory(response.data.data || []);
        setTotalPages(response.data.pagination?.totalPages || 0);
        setCurrentPage(response.data.pagination?.currentPage || 1);
      } else {
        throw new Error(response.data?.message || "Failed to fetch history");
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
      setReport({
        show: true,
        message: err.message || "Failed to load login history.",
        type: "error",
      });
      setHistory([]);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]); // Refetch when currentPage changes

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const renderPaginationItems = () => {
    let items = [];
    if (totalPages <= 1) return null; // No pagination needed for 0 or 1 page

    // Always show First and Previous
    items.push(
      <Pagination.First
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    // Determine page numbers to display (e.g., current +/- 2)
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    if (startPage > 1) {
      items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

     if (endPage < totalPages) {
      items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
    }

    // Always show Next and Last
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  // Helper to truncate agent string and provide full details in tooltip
  const renderAgent = (agentString) => {
    if (!agentString) return "-";
    const maxLength = 30; // Max length before truncating
    const truncated = agentString.length > maxLength
      ? `${agentString.substring(0, maxLength)}...`
      : agentString;

    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={`tooltip-agent-${agentString.substring(0, 10)}`}>{agentString}</Tooltip>}
      >
        <span>{truncated}</span>
      </OverlayTrigger>
    );
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-light py-3">
        <Card.Title as="h5" className="mb-0">
          <FaHistory className="me-2" /> Login History
        </Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : history.length === 0 ? (
          <Alert variant="info">No login history found.</Alert>
        ) : (
          <>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>Login Date</th>
                  <th>Method</th>
                  <th>IP Address</th> {/* Added IP Address column */}
                  <th>Device/Browser</th> {/* Added Agent column */}
                  <th>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => (
                  <tr key={index}>
                    <td>
                      {moment(item.created_at).format("YYYY-MM-DD HH:mm:ss")}
                    </td>
                    <td>{item.incident || "-"}</td>
                    <td>{item.ip || "-"}</td> {/* Display IP */}
                    <td>{renderAgent(item.agent)}</td> {/* Display Agent */}
                    <td>
                      {moment(item.updated_at).format("YYYY-MM-DD HH:mm:ss")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {totalPages > 1 && (
              <div className="d-flex justify-content-center">
                 <Pagination size="sm">{renderPaginationItems()}</Pagination>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default LoginHistoryCard;

